import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { Building2, CircleDot } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import {
  DataTableFilter,
  RowDataWithActions,
} from '@/components/ui/data-table';
import { DataTableColumnHeader } from '@/components/ui/data-table/data-table-column-header';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  isNil,
  SwaggerAudience,
  SwaggerDefinitionStatus,
  SwaggerDefinitionSummary,
} from '@activepieces/shared';

/** Client badges spelled out in a row before it falls back to a counter. */
const MAX_CLIENT_BADGES = 3;

const STATUS_STYLES: Record<SwaggerDefinitionStatus, string> = {
  [SwaggerDefinitionStatus.ENABLED]:
    'bg-green-100 text-green-700 border-green-200',
  [SwaggerDefinitionStatus.DRAFT]:
    'bg-amber-100 text-amber-700 border-amber-200',
  [SwaggerDefinitionStatus.DISABLED]:
    'bg-muted text-muted-foreground border-border',
};

export const swaggerStatusLabel = (status: SwaggerDefinitionStatus): string => {
  switch (status) {
    case SwaggerDefinitionStatus.ENABLED:
      return t('Enabled');
    case SwaggerDefinitionStatus.DRAFT:
      return t('Draft');
    case SwaggerDefinitionStatus.DISABLED:
      return t('Disabled');
  }
};

export const SwaggerStatusBadge = ({
  status,
}: {
  status: SwaggerDefinitionStatus;
}) => (
  <Badge variant="outline" className={STATUS_STYLES[status]}>
    {swaggerStatusLabel(status)}
  </Badge>
);

export const useAdminSwaggersTable = () => {
  const columns: ColumnDef<RowDataWithActions<SwaggerDefinitionSummary>>[] = [
    {
      accessorKey: 'displayName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Swagger')} />
      ),
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-medium">{row.original.displayName}</span>
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-1.5">
            <span>{row.original.pieceName}</span>
            <CopyButton
              textToCopy={row.original.pieceName}
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              withoutTooltip
            />
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      // `tenantIds` rather than `tenantNames`: the column id is what the
      // `Client ID` filter below writes to the URL, and what the server then
      // filters on. A filter whose `accessorKey` matches no column silently
      // writes `?undefined=`.
      accessorKey: 'tenantIds',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Clients')} />
      ),
      cell: ({ row }) => {
        if (row.original.audience === SwaggerAudience.ALL) {
          return <Badge variant="outline">{t('All clients')}</Badge>;
        }
        if (row.original.tenantIds.length === 0) {
          return (
            <span className="text-muted-foreground">{t('Unassigned')}</span>
          );
        }
        // Capped: a row that grows with the assignment pushes every other
        // column out of the viewport, and the exact list is on the detail page.
        const shown = row.original.tenantIds.slice(0, MAX_CLIENT_BADGES);
        const overflow = row.original.tenantIds.length - shown.length;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {shown.map((tenantId, index) => {
              const clientName = row.original.tenantNames[index];
              const customerId = row.original.tenantCustomerIds[index];
              // The Kizeo client number, not the company name: it is what the
              // clients table, a support ticket and the logs all identify a
              // client by. The name stays one hover away — and stands in for a
              // tenant that has no number at all.
              const badge = (
                <Badge
                  variant="secondary"
                  className={cn(
                    'font-normal',
                    !isNil(customerId) && 'font-mono',
                  )}
                >
                  {customerId ?? clientName ?? tenantId}
                </Badge>
              );
              if (isNil(customerId) || isNil(clientName)) {
                return <span key={tenantId}>{badge}</span>;
              }
              return (
                <Tooltip key={tenantId}>
                  {/* `Badge` renders a plain div and forwards no ref, so the
                      trigger anchors on a wrapper. */}
                  <TooltipTrigger asChild>
                    <span>{badge}</span>
                  </TooltipTrigger>
                  <TooltipContent>{clientName}</TooltipContent>
                </Tooltip>
              );
            })}
            {overflow > 0 && (
              <span className="text-xs text-muted-foreground">
                {t('+{count}', { count: overflow })}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'baseUrl',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Base URL')} />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.baseUrl}</span>
      ),
    },
    {
      accessorKey: 'enabledOperationsCount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Routes')} />
      ),
      cell: ({ row }) => (
        <span>
          {row.original.enabledOperationsCount}
          <span className="text-muted-foreground">
            {' / '}
            {row.original.operationsCount}
          </span>
        </span>
      ),
    },
    {
      accessorKey: 'originalSpecVersion',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Spec')} />
      ),
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.originalSpecVersion}</Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Status')} />
      ),
      cell: ({ row }) => <SwaggerStatusBadge status={row.original.status} />,
    },
  ];

  const filters: DataTableFilter<keyof SwaggerDefinitionSummary>[] = [
    {
      type: 'input',
      title: t('Search'),
      accessorKey: 'displayName',
      options: [],
    },
    {
      type: 'input',
      title: t('Client ID'),
      accessorKey: 'tenantIds',
      icon: Building2,
      options: [],
    },
    {
      type: 'select',
      title: t('Status'),
      accessorKey: 'status',
      icon: CircleDot,
      options: [
        {
          label: swaggerStatusLabel(SwaggerDefinitionStatus.ENABLED),
          value: SwaggerDefinitionStatus.ENABLED,
        },
        {
          label: swaggerStatusLabel(SwaggerDefinitionStatus.DRAFT),
          value: SwaggerDefinitionStatus.DRAFT,
        },
        {
          label: swaggerStatusLabel(SwaggerDefinitionStatus.DISABLED),
          value: SwaggerDefinitionStatus.DISABLED,
        },
      ],
    },
  ];

  return { columns, filters };
};
